import React, { useState, useEffect } from "react";
import {
  ChevronDown,
  GraduationCap,
  BookOpen,
  Award,
  Sparkles,
  ChevronRight,
} from "lucide-react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import ContactModal from "../../components/User/Home/ContactModal";

const CoursesDetails = () => {
  const location = useLocation();
  const [courseData, setCourseData] = useState(null);
  const [selectedProgram, setSelectedProgram] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    const fetchCourseData = async () => {
      try {
        setLoading(true);
        const courseId = location.state?.courseId;

        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/courses/get-course-by-id/${courseId}`,
        );

        if (response.data.success) {
          setCourseData(response.data.data);
          setSelectedProgram(0);
        }
      } catch (err) {
        setError(err.message);
        console.error("Error fetching course data:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchCourseData();
  }, [location]);

  if (loading) {
    return <ProgramDetailsSkeleton />;
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 text-lg mb-4">
            Error loading course: {error}
          </p>
          <button
            onClick={() => window.location.reload()}
            className="bg-[#DA3335] text-white px-6 py-2 rounded-lg hover:bg-[#c22d2f]"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!courseData || !courseData.programs || courseData.programs.length === 0) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <p className="text-gray-600 text-lg">No course data available</p>
      </div>
    );
  }

  const currentProgram = courseData.programs[selectedProgram];

  // Organize curriculum by year
  const organizeCurriculum = () => {
    const years = {};
    currentProgram.curriculum?.forEach((sem) => {
      if (!years[sem.year]) {
        years[sem.year] = [];
      }
      years[sem.year].push(sem);
    });
    return years;
  };

  const curriculumByYear = organizeCurriculum();

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Image Section */}
      {/* Hero Image Section */}
      <div className="w-full">
        <div className="relative h-[450px] bg-gradient-to-r from-gray-800 to-gray-900">
          {/* Category Badge - Hidden on small screens, visible on medium and up */}
          <div className="hidden md:block absolute top-1/2 left-20 z-20">
            <div className=" bg-gradient-to-r from-gray-900 via-[#DA3335] to-[#144982] bg-clip-text text-transparent px-8 py-4">
              <span className="font-bold text-2xl lg:text-5xl tracking-wider heading whitespace-nowrap">
                {courseData.category}
              </span>
            </div>
          </div>

          <div className="absolute inset-0 bg-black opacity-30"></div>
          <img
            src={courseData.img}
            alt={courseData.name}
            className="w-full h-full object-cover"
          />
        </div>
      </div>

      {/* Title Section */}
      <div className="w-full mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1 className="text-4xl md:text-5xl font-bold text-center text-gray-900 mb-4 heading">
          {courseData.name}
        </h1>
        <p className="text-lg text-gray-700 text-center max-w-7xl mx-auto leading-relaxed mb-8 sub-heading tracking-wide">
          {courseData.description}
        </p>

        {/* Program Selector */}
        <div className="flex justify-center gap-4 mb-8 flex-wrap">
          {courseData.programs.map((program, idx) => (
            <button
              key={idx}
              onClick={() => setSelectedProgram(idx)}
              className={`px-6 py-3 rounded-lg font-semibold transition-all duration-300 heading ${
                selectedProgram === idx
                  ? "bg-gradient-to-r from-[#DA3335] to-[#ff4547] text-white shadow-lg"
                  : "bg-gray-200 text-gray-700 hover:bg-gray-300"
              }`}
            >
              {program.programType}
              {" : "}
              {program.duration}
            </button>
          ))}
        </div>

        {/* Program Details */}
        <div className="flex flex-wrap justify-center gap-6 text-center sub-heading">
          <div className="bg-blue-50 px-6 py-3 rounded-lg">
            <p className="text-sm text-gray-600">Duration</p>
            <p className="text-lg font-bold text-[#144982]">
              {currentProgram.duration}
            </p>
          </div>
          <div className="bg-blue-50 px-6 py-3 rounded-lg">
            <p className="text-sm text-gray-600">Eligibility</p>
            <p className="text-lg font-bold text-[#144982]">
              {currentProgram.eligibility}
            </p>
          </div>
          {/* <div className="bg-blue-50 px-6 py-3 rounded-lg">
            <p className="text-sm text-gray-600">Credits</p>
            <p className="text-lg font-bold text-[#144982]">
              {currentProgram.credits}
            </p>
          </div> */}
          <div className="bg-blue-50 px-6 py-3 rounded-lg">
            <p className="text-sm text-gray-600">Program</p>
            <p className="text-lg font-bold text-[#144982]">
              {currentProgram.programType}
            </p>
          </div>
          <div className="bg-blue-50 px-6 py-3 rounded-lg">
            <p className="text-sm text-gray-600">Course Fee per Annum</p>
            <p className="text-lg font-bold text-[#144982]">
              ₹{currentProgram.onlineApplicationFee}
            </p>
          </div>
        </div>
      </div>

      {/* Institution Description */}
      <div className="w-full mx-auto px-4 sm:px-6 lg:px-8 pb-12">
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-8 rounded-xl">
          <h2 className="text-2xl font-bold text-[#144982] mb-4 heading">
            About {courseData.instituteName}
          </h2>
          <p className="text-lg text-gray-700 leading-relaxed mb-4 sub-heading">
            {courseData.instituteName} is located at {courseData.location}. This
            program is offered in technical collaboration with{" "}
            {courseData.universityCollaboration}.
          </p>
          <p className="text-lg text-gray-700 leading-relaxed sub-heading">
            Explore the {courseData.name} program which is one of the booming
            industries. Graduates of this program are qualified to take on
            various professional roles including planning and concept
            management, marketing, production management, and more.
          </p>
        </div>
      </div>

      {/* Enquire Now Button */}
      <div className="w-full mx-auto px-4 sm:px-6 lg:px-8 pb-12 text-center">
        <button
          // onClick={() => (window.location.href = "/contact")}
          onClick={() => setIsModalOpen(true)}
          className="bg-gradient-to-r from-[#DA3335] to-[#ff4547] hover:from-[#c22d2f] hover:to-[#DA3335] text-white font-bold py-4 px-12 rounded-full shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-300"
        >
          Enquire Now
        </button>
        <ContactModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
        />
      </div>

      {/* Career Paths Section */}
      <div className="bg-gradient-to-br from-gray-50 to-blue-50 py-8">
        <div className="w-full mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center text-gray-900 mb-4 heading">
            Career Paths
          </h2>
          <p className="text-center text-gray-600 mb-12 text-lg sub-heading">
            Explore the exciting opportunities that await you
          </p>

          <div className="flex flex-col lg:flex-row gap-8 items-stretch">
            {/* Career List */}
            <div className="w-full">
              <div className="bg-white rounded-xl shadow-2xl p-8 h-full">
                <h3 className="text-2xl font-bold text-[#144982] mb-6 border-b-4 border-[#DA3335] pb-3 inline-block heading">
                  Your Future Awaits
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sub-heading">
                  {courseData.careerPaths?.map((career, idx) => (
                    <div key={idx} className="group">
                      <div
                        className={`${
                          idx % 2 === 0
                            ? "bg-gradient-to-r from-[#144982] to-[#1a5a9e]"
                            : "bg-gradient-to-r from-[#DA3335] to-[#ff4547]"
                        } text-white p-4 rounded-lg shadow-md hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300`}
                      >
                        <div className="flex items-center">
                          <div
                            className={`w-2 h-2 ${
                              idx % 2 === 0 ? "bg-[#DA3335]" : "bg-[#144982]"
                            } rounded-full mr-3`}
                          ></div>
                          <span className="font-semibold text-sm">
                            {career}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Curriculum Section */}
      <div className="py-8 bg-white">
        <div className="w-full mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Header */}
          <div className="text-center mb-16">
            <div className="inline-block mb-4">
              <Sparkles className="w-12 h-12 text-[#DA3335] mx-auto mb-2" />
            </div>
            <h2 className="text-5xl font-extrabold text-gray-900 mb-4 heading">
              Course Curriculum
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto sub-heading">
              Comprehensive curriculum for {courseData.name} -{" "}
              {currentProgram.programType}
            </p>
            <div className="mt-4 h-1 w-32 bg-gradient-to-r from-[#DA3335] to-[#144982] mx-auto rounded-full"></div>
            <p className="text-sm text-gray-500 mt-4 uppercase tracking-wide sub-heading">
              {courseData.universityCollaboration}
            </p>
          </div>

          {/* Curriculum Display */}
          <div className="w-full mx-auto">
            {Object.keys(curriculumByYear).length > 0 ? (
              <div className="bg-gradient-to-br from-white to-blue-50 rounded-3xl shadow-2xl overflow-hidden border-2 border-[#144982]">
                <div className="bg-gradient-to-r from-[#144982] to-[#1a5a9e] p-8">
                  <div className="flex items-center gap-6">
                    <div className="bg-white/20 p-4 rounded-2xl backdrop-blur-sm">
                      <GraduationCap className="w-12 h-12 text-white" />
                    </div>
                    <div>
                      <h3 className="text-3xl font-bold text-white mb-2 heading">
                        {courseData.name}
                      </h3>
                      <p className="text-blue-100 text-lg sub-heading">
                        {currentProgram.duration} | {currentProgram.programType}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="p-8 bg-white">
                  {Object.keys(curriculumByYear).map((year, yearIdx) => (
                    <div key={yearIdx} className="mb-8 last:mb-0 sub-heading">
                      <h4 className="font-bold text-[#144982] mb-6 text-2xl flex items-center gap-3">
                        <div className="w-2 h-8 bg-[#DA3335] rounded"></div>
                        {year}
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {curriculumByYear[year].map((sem, semIdx) => (
                          <div
                            key={semIdx}
                            className="bg-gradient-to-br from-blue-50 to-indigo-50 p-6 rounded-2xl border-l-4 border-[#144982] hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1"
                          >
                            <div className="flex items-center gap-3 mb-4">
                              <div className="bg-[#144982] p-2 rounded-lg">
                                <BookOpen className="w-5 h-5 text-white" />
                              </div>
                              <h4 className="font-bold text-[#144982] text-lg">
                                {sem.semester}
                              </h4>
                            </div>
                            <div className="space-y-3">
                              {sem.subjects.map((subject, subIdx) => (
                                <div
                                  key={subIdx}
                                  className="flex items-start gap-3 bg-white/60 p-3 rounded-lg backdrop-blur-sm"
                                >
                                  <ChevronRight className="w-4 h-4 text-[#DA3335] mt-0.5 flex-shrink-0" />
                                  <div>
                                    <span className="text-sm text-gray-700 leading-relaxed font-medium">
                                      {subject.name}
                                    </span>
                                    <span className="text-xs text-gray-500 ml-2 capitalize">
                                      ({subject.type})
                                    </span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="text-center py-12 bg-gray-50 rounded-xl">
                <p className="text-gray-600 text-lg">
                  No curriculum information available for this program
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Get Started Section */}
      <div className="bg-gradient-to-br from-gray-50 to-blue-50 py-16">
        <div className="w-full mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center text-gray-900 mb-4 heading">
            GET STARTED
          </h2>
          <p className="text-center text-gray-600 mb-12 text-lg sub-heading">
            Your journey to success begins here
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white rounded-xl shadow-2xl overflow-hidden transform hover:scale-105 transition-all duration-300">
              <img
                src="https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=400"
                alt="Gain Experience"
                className="w-full h-48 object-cover"
              />
              <div className="p-6">
                <div className="bg-gradient-to-r from-[#144982] to-[#1a5a9e] text-white text-center py-3 rounded-lg mb-4">
                  <h3 className="text-xl font-bold heading">GAIN EXPERIENCE</h3>
                </div>
                <p className="text-gray-700 text-sm leading-relaxed sub-heading">
                  As you gain experience, you gain knowledge and with that
                  knowledge comes power. Learn from your mistakes and successes.
                  Analyze positive experiences to ensure they happen again. The
                  experience of achieving success through setting carefully
                  planned goals helps to ensure success through gaining greater
                  self-confidence.
                </p>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-2xl overflow-hidden transform hover:scale-105 transition-all duration-300">
              <img
                src="https://images.unsplash.com/photo-1529156069898-49953e39b3ac?w=400"
                alt="Have the Right Attitude"
                className="w-full h-48 object-cover"
              />
              <div className="p-6">
                <div className="bg-gradient-to-r from-[#144982] to-[#1a5a9e] text-white text-center py-3 rounded-lg mb-4">
                  <h3 className="text-xl font-bold heading">RIGHT ATTITUDE</h3>
                </div>
                <p className="text-gray-700 text-sm leading-relaxed sub-heading">
                  Having the right attitude is essential for success. Stay
                  positive, be persistent, and embrace challenges as
                  opportunities for growth. Your mindset determines your
                  approach to learning and your ability to overcome obstacles in
                  your educational journey.
                </p>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-2xl overflow-hidden transform hover:scale-105 transition-all duration-300">
              <img
                src="https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=400"
                alt="Build Your Future"
                className="w-full h-48 object-cover"
              />
              <div className="p-6">
                <div className="bg-gradient-to-r from-[#144982] to-[#1a5a9e] text-white text-center py-3 rounded-lg mb-4">
                  <h3 className="text-xl font-bold heading">
                    BUILD YOUR FUTURE
                  </h3>
                </div>
                <p className="text-gray-700 text-sm leading-relaxed sub-heading">
                  Start building your future today with dedication and hard
                  work. Set clear goals, develop your skills continuously, and
                  network with industry professionals. Your education is the
                  foundation for a successful and rewarding career.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CoursesDetails;

const ProgramDetailsSkeleton = () => {
  return (
    <div className="animate-pulse">
      {/* Hero section skeleton */}
      <div className="h-60 bg-gray-300 dark:bg-gray-700 rounded-b-2xl"></div>

      <div className="max-w-6xl mx-auto px-6 py-12 text-center">
        {/* Title */}
        <div className="h-8 bg-gray-300 dark:bg-gray-700 w-2/3 mx-auto rounded mb-4"></div>

        {/* Description */}
        <div className="space-y-2 mb-8">
          <div className="h-4 bg-gray-300 dark:bg-gray-700 w-5/6 mx-auto rounded"></div>
          <div className="h-4 bg-gray-300 dark:bg-gray-700 w-4/6 mx-auto rounded"></div>
        </div>

        {/* Buttons */}
        <div className="flex justify-center gap-4 mb-10">
          <div className="h-10 w-28 bg-gray-300 dark:bg-gray-700 rounded-lg"></div>
          <div className="h-10 w-28 bg-gray-300 dark:bg-gray-700 rounded-lg"></div>
        </div>

        {/* Info boxes */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 max-w-4xl mx-auto">
          {[...Array(5)].map((_, i) => (
            <div
              key={i}
              className="flex flex-col items-center gap-2 bg-gray-100 dark:bg-gray-800 rounded-xl p-4"
            >
              <div className="h-3 w-16 bg-gray-300 dark:bg-gray-700 rounded"></div>
              <div className="h-4 w-20 bg-gray-300 dark:bg-gray-700 rounded"></div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
